import React, { useEffect, useState } from 'react';
import { isMobileContainer } from '../../../utils/device';
import { useAuth } from '../../../context/AuthContext';
import { canManageCrmEmployees } from '../../../utils/crmPermissions';
import useCrmMail from '../../../hooks/useCrmMail';
import { useToast } from '../../../hooks/use-toast';
import CrmMailSidebar from './CrmMailSidebar';
import CrmMailSearchBar, { CrmMailList } from './CrmMailList';
import CrmMailPreview from './CrmMailPreview';
import CrmMailCompose from './CrmMailCompose';
import CrmMailEmptyState from './CrmMailEmptyState';
import CrmEmployeeMailSettings from '../CrmEmployeeMailSettings';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../ui/dialog';

export default function CrmMail() {
  const mail = useCrmMail();
  const { toast } = useToast();
  const { user } = useAuth();
  const isMobile = isMobileContainer();
  const canConfigure = canManageCrmEmployees(user?.role_slug);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Mobile panes: folders → list → preview
  const [mobilePane, setMobilePane] = useState('folders');

  useEffect(() => {
    if (!isMobile) return;
    if (mail.selectedId) {
      setMobilePane('preview');
    } else if (mobilePane === 'preview') {
      setMobilePane('list');
    }
  }, [isMobile, mail.selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const showFolders = !isMobile || mobilePane === 'folders';
  const showList = !isMobile || mobilePane === 'list';
  const showPreview = !isMobile || mobilePane === 'preview';

  const handleSelectFolder = (folder) => {
    mail.selectFolder(folder);
    if (isMobile) setMobilePane('list');
  };

  const handleBackToFolders = () => {
    mail.clearSelection();
    setMobilePane('folders');
  };

  const handleBackToList = () => {
    mail.clearSelection();
    setMobilePane('list');
  };

  const handleSend = async (flushedCompose) => {
    try {
      await mail.sendMail(flushedCompose);
      toast({ title: 'Sent', description: 'Email sent successfully' });
    } catch (err) {
      toast({
        title: 'Send failed',
        description: err.response?.data?.message || 'Could not send email',
        variant: 'destructive',
      });
    }
  };

  const handleUpload = async (file) => {
    try {
      await mail.uploadAttachment(file);
      toast({ title: 'Attached', description: file.name });
    } catch (err) {
      toast({
        title: 'Upload failed',
        description: err.response?.data?.message || 'Could not upload file',
        variant: 'destructive',
      });
      throw err;
    }
  };

  const handleSettingsSaved = async () => {
    await mail.loadStatus();
    setSettingsOpen(false);
    if (isMobile) setMobilePane('list');
  };

  const settingsDialog = (
    <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Configure Mail</DialogTitle>
          <DialogDescription>
            Enter IMAP and SMTP details for your mailbox.
          </DialogDescription>
        </DialogHeader>
        {settingsOpen && (
          <CrmEmployeeMailSettings
            self
            employeeName={[user?.first_name, user?.last_name].filter(Boolean).join(' ')}
            embedded
            onSaved={handleSettingsSaved}
          />
        )}
      </DialogContent>
    </Dialog>
  );

  if (!mail.status.configured && !mail.loadingList) {
    return (
      <div className="h-[calc(100vh-8rem)] border border-border rounded-xl bg-card overflow-hidden">
        <CrmMailEmptyState
          variant="not-configured"
          canConfigure={canConfigure}
          onConfigure={() => setSettingsOpen(true)}
        />
        {canConfigure && settingsDialog}
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-8rem)] border border-border rounded-xl bg-card overflow-hidden flex flex-col" data-testid="crm-mail-page">
      {(mail.error || mail.status.last_sync_error) && (
        <div className="px-4 py-2 text-sm bg-destructive/10 text-destructive border-b border-destructive/20">
          {mail.error || mail.status.last_sync_error}
        </div>
      )}
      <div className="flex flex-1 min-h-0">
        {showFolders && (
          <CrmMailSidebar
            folder={mail.folder}
            unreadCounts={mail.status.unread_counts}
            onSelectFolder={handleSelectFolder}
            onCompose={() => mail.openCompose()}
            onSync={mail.manualSync}
            syncing={mail.syncing}
            lastSyncAt={mail.status.last_sync_at}
            lastSyncError={mail.status.last_sync_error}
            canConfigure={canConfigure}
            onConfigure={() => setSettingsOpen(true)}
            fullWidth={isMobile}
          />
        )}

        {showList && (
          <div className={`flex flex-col border-r border-border min-h-0 ${isMobile ? 'flex-1 w-full' : 'w-full max-w-[420px]'}`}>
            {isMobile && (
              <button
                type="button"
                className="text-sm text-blue-600 px-4 py-2 border-b text-left shrink-0"
                onClick={handleBackToFolders}
              >
                ← Folders
              </button>
            )}
            <CrmMailSearchBar
              searchQuery={mail.searchQuery}
              onSearchChange={mail.setSearchQuery}
              filter={mail.filter}
              onFilterChange={mail.setFilter}
            />
            {!mail.messages.length && !mail.loadingList ? (
              <CrmMailEmptyState variant="folder" onCompose={() => mail.openCompose()} />
            ) : (
              <CrmMailList
                messages={mail.messages}
                selectedId={mail.selectedId}
                loading={mail.loadingList}
                onSelect={mail.loadMessage}
                folder={mail.folder}
              />
            )}
          </div>
        )}

        {showPreview && (
          <div className="flex-1 min-w-0 flex flex-col">
            {isMobile && mail.selectedId && (
              <button
                type="button"
                className="text-sm text-blue-600 px-4 py-2 border-b text-left"
                onClick={handleBackToList}
              >
                ← Back to list
              </button>
            )}
            {mail.selectedMessage ? (
              <CrmMailPreview
                message={mail.selectedMessage}
                thread={mail.thread}
                loading={mail.loadingMessage}
                onReply={() => mail.openCompose('reply', mail.selectedMessage)}
                onReplyAll={() => mail.openCompose('reply_all', mail.selectedMessage)}
                onForward={() => mail.openCompose('forward', mail.selectedMessage)}
                onAction={(action) => mail.performAction(mail.selectedId, action)}
              />
            ) : (
              <CrmMailEmptyState variant="no-selection" />
            )}
          </div>
        )}
      </div>

      <CrmMailCompose
        open={mail.composeOpen}
        onClose={mail.closeCompose}
        compose={mail.compose}
        setCompose={mail.setCompose}
        mode={mail.composeMode}
        sending={mail.sending}
        onSend={handleSend}
        onSearchRecipients={mail.searchRecipients}
        recipients={mail.recipients}
        onUpload={handleUpload}
        uploadProgress={mail.uploadProgress}
      />

      {canConfigure && settingsDialog}
    </div>
  );
}
